/* LEGEND — classic trend lines on the 1-minute chart (StockCharts "Trend Lines").
   Pure, incremental, forward-only engine; no React, no chart, no I/O. The
   TrendLineLayer feeds it the pane's own candles and maps the resulting frame
   (time + price) to pixels.

   THE PICTURE   A ball bouncing down a staircase. Lay a ruler across the tops
                 of the bounces: it slopes down — that is RESISTANCE. Lay a ruler
                 under the spots it lands on: it slopes up — that is SUPPORT.
                 Both rulers stick out to the right. Where they squeeze together
                 the ball has to burst out one way or the other.

   RESISTANCE    Anchor = the HIGH OF THE DAY (highest wick so far, volume-guarded;
                 a re-touch of that high moves the anchor to the latest touch and
                 retires any ruler from the old one — the top the move falls from). The ruler
                 rests on the outermost confirmed lower swing high after the anchor:
                 of all candidates the one whose line is highest (max slope), so
                 every other bounce top sits under it. Extended right.
   SUPPORT       Mirror image. Anchor = the LOW OF THE DAY (lowest wick so far,
                 volume-guarded; a re-touch moves the anchor to the latest touch and
                 retires any ruler from the old one — the bottom the move launches
                 from, never the first bar of a flat base). The ruler
                 rests under the outermost confirmed higher swing low after the
                 anchor (min slope), so every other landing sits above it.
   SWING         a bar whose high is strictly above the N completed bars on each
                 side (mirror for lows). Known N bars after it prints — a ruler
                 appears N bars after the bounce it rests on, never back-placed.
   TOUCHES       bars whose wick comes within tolerance of the ruler. Two touches
                 is a guess, three or more is a line you trust — reported, not enforced.
   WOBBLES       a wick past the ruler by less than `tolerance` = max(0.1% of price,
                 one NBBO spread, one tick) is ignored.
   BREAK         a wick past the ruler by more than tolerance. The ruler is NOT
                 thrown away: it is marked with an × where it happened, kept on the
                 chart (still extended right) and a new ruler is laid from the same
                 anchor across the next confirmed bounce at or after the break.
                 The forming bar's wick counts (a wick, once printed, stays printed).
                 The first COMPLETED bar closing above resistance is recorded
                 separately (closedAboveAt) — a different observation.
   NEW EXTREME   a new high of the day retires the resistance ruler (superseded,
                 kept faded) and starts over from the new top; a new low of the day
                 does the same for support. Anchors of a drawn ruler never move.
   SESSION       04:00–20:00 ET, one session per ET date; everything, history
                 included, is per session. */

export type TlBar = { time: number; open: number; high: number; low: number; close: number; value?: number };

export type TlParams = {
  swingBars: number;          // N — completed bars on each side of a swing
  tolerancePct: number;       // wobble tolerance as a fraction of price (floored at one spread / one tick)
  volumeGuardPct: number;     // an anchor bar must have volume ≥ this percentile of session bar volume so far (0 = off)
  volumeGuardMinBars: number; // the guard is vacuous until this many session bars have completed
  sessionStartMin: number;    // minutes after midnight ET
  sessionEndMin: number;
  sessionScoped: boolean;     // true: one session per ET date (intraday TFs); false: the whole series is one session (1H and up)
  keepHistory: boolean;
};

export const TL_DEFAULTS: TlParams = {
  swingBars: 3, tolerancePct: 0.001, volumeGuardPct: 20, volumeGuardMinBars: 10,
  sessionStartMin: 4 * 60, sessionEndMin: 20 * 60, sessionScoped: true, keepHistory: true,
};
export const TL_COLORS = { upper: '#f0883e', lower: '#38c08a' };
export const TL_PARAMS_KEY = 'legend.trend.params';

// Parameters are tunable without a redeploy: a JSON object under
// localStorage[TL_PARAMS_KEY] (e.g. {"swingBars":2}) overrides the defaults.
// Re-read on every engine rebuild (toggle the pill off/on to apply).
export function getTrendParams(): TlParams {
  let o: any = null;
  try { const ls: any = (globalThis as any).localStorage; const raw = ls ? ls.getItem(TL_PARAMS_KEY) : null; o = raw ? JSON.parse(raw) : null; } catch { o = null; }
  const p: TlParams = { ...TL_DEFAULTS };
  if (o && typeof o === 'object') {
    for (const k of Object.keys(TL_DEFAULTS) as (keyof TlParams)[]) {
      const v = o[k];
      if (k === 'keepHistory' || k === 'sessionScoped') { if (typeof v === 'boolean') (p as any)[k] = v; }
      else if (typeof v === 'number' && Number.isFinite(v)) (p as any)[k] = v;
    }
  }
  p.swingBars = Math.max(1, Math.floor(p.swingBars));
  return p;
}

export type TlKind = 'upper' | 'lower';
export type TlStatus = 'active' | 'broken' | 'superseded';

export type TlLine = {
  id: number;
  kind: TlKind;
  status: TlStatus;
  i1: number; i2: number;        // anchor bar indices (session bar index)
  t1: number; t2: number;        // anchor bar times (unix sec)
  p1: number; p2: number;        // anchor wick prices
  slope: number;                 // price per bar
  tol: number;                   // tolerance frozen at draw time
  touches: number;               // bars whose wick came within tolerance of the ruler (anchors included)
  drawableAt: number;            // bar index at which the ruler was laid (= second anchor confirmed)
  drawableTime: number;
  brokenAt: number;              // bar index of the wick break, or -1
  brokenTime: number | null;
  brokenPrice: number | null;    // ruler value at the break bar (where the × sits)
  closedAboveAt: number;         // upper only: first COMPLETED bar closing above the ruler, or -1
  closedAboveTime: number | null;
  endedAt: number;               // bar index at which the ruler stopped being current (broken or superseded), or -1
  endedTime: number | null;
};

export type TlAnchor = { label: 'H1' | 'H2' | 'L1' | 'L2'; kind: TlKind; i: number; t: number; price: number };
type Pt = { i: number; t: number; price: number };

export type TlFrame = {
  ok: boolean;
  reason: 'warming' | 'no-session' | null;
  n: number;                     // completed session bars
  N: number;
  sessionDate: string | null;
  tol: number;
  h1: Pt | null;                 // high of the day (current resistance anchor)
  l1: Pt | null;                 // low of the day (current support anchor)
  upperState: 'ok' | 'broken' | 'none';
  lowerState: 'ok' | 'broken' | 'none';
  upper: { dir: 'falling'; crossed: boolean; closedAbove: boolean; touches: number; valueNow: number } | null;
  lower: { dir: 'rising'; broken: boolean; touches: number; valueNow: number } | null;
  upperLineId: number;           // id of the current upper ruler (active or broken), -1 if none
  lowerLineId: number;
  squeeze: boolean;
  lines: TlLine[];               // history + current, oldest first (data — the layer does NOT draw all of these)
  display: TlLine[];             // what to draw: per side the current ruler, or the latest previous one if there is no current
  anchors: TlAnchor[];           // H1/H2/L1/L2 of the CURRENT rulers
  counts: { history: number };
};

// ---- ET clock: one session per America/New_York trading date ----
const ET_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
});
export function etParts(sec: number): { date: string; min: number } {
  const parts = ET_FMT.formatToParts(new Date(sec * 1000));
  const g = (t: string) => { const p = parts.find((x) => x.type === t); return p ? p.value : '00'; };
  const hh = +g('hour') % 24;
  return { date: g('year') + '-' + g('month') + '-' + g('day'), min: hh * 60 + +g('minute') };
}

const lineVal = (ln: { i1: number; p1: number; slope: number }, i: number) => ln.p1 + ln.slope * (i - ln.i1);
export const trendLineValue = lineVal;

function percentile(sorted: number[], pct: number): number {
  if (!sorted.length) return 0;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((pct / 100) * (sorted.length - 1))));
  return sorted[idx] as number;
}

export class PullbackTrendEngine {
  params: TlParams;
  bars: TlBar[] = [];
  sessionDate: string | null = null;
  pivotHighs: number[] = [];
  pivotLows: number[] = [];
  h1: Pt | null = null;          // high of the day
  l1: Pt | null = null;          // low of the day
  upper: TlLine | null = null;   // current resistance ruler (active or broken) — stays drawn until replaced
  lower: TlLine | null = null;   // current support ruler
  history: TlLine[] = [];        // superseded + replaced-after-break rulers, oldest first
  version = 0;                   // bumps on every state mutation (paint-key input)
  private upperMinIdx = 0;       // after a break, the next bounce must be at/after the breaking bar
  private lowerMinIdx = 0;
  private volumes: number[] = [];
  private spread = 0;
  private nextId = 1;

  constructor(params?: Partial<TlParams>) {
    this.params = { ...TL_DEFAULTS, ...(params || {}) };
    this.params.swingBars = Math.max(1, Math.floor(this.params.swingBars));
  }

  reset() {
    this.bars = []; this.sessionDate = null; this.pivotHighs = []; this.pivotLows = [];
    this.h1 = null; this.l1 = null; this.upper = null; this.lower = null; this.history = [];
    this.upperMinIdx = 0; this.lowerMinIdx = 0;
    this.volumes = []; this.version++;
  }

  // Latest NBBO spread (ask − bid) — the tolerance floor. Optional.
  setSpread(s: number | null | undefined) {
    this.spread = Number.isFinite(s as number) && (s as number) > 0 ? (s as number) : 0;
  }

  private tolFor(price: number): number {
    const tick = price < 1 ? 0.001 : 0.01;
    return Math.max(price * this.params.tolerancePct, this.spread, tick);
  }

  private inSession(sec: number): { date: string; min: number } | null {
    if (!this.params.sessionScoped) return { date: 'all', min: 0 };
    const et = etParts(sec);
    if (et.min < this.params.sessionStartMin || et.min >= this.params.sessionEndMin) return null;
    return et;
  }

  // ---- completed bar ----
  push(bar: TlBar) {
    const et = this.inSession(bar.time);
    if (!et) return;
    if (et.date !== this.sessionDate) { this.reset(); this.sessionDate = et.date; }
    const i = this.bars.length;
    this.bars.push(bar);
    this.version++;
    const tol = this.tolFor(bar.close);

    // (a) does this bar break a current ruler? (checked BEFORE any new-extreme
    //     reset, so a new-high bar that also crossed the ruler reads as a break)
    this.checkBreaks(i, bar, false);

    // (b) high / low of the day (volume-guarded anchors)
    const vol = Number(bar.value) || 0;
    const passes = this.passesGuard(vol);
    this.volumes.push(vol);
    if (passes) {
      // A re-touch of the extreme (within tolerance) moves the anchor to the
      // latest touch and retires any ruler laid from the old one: the base is
      // still forming, and the ruler must start where the move actually
      // launches from — not from the first bar of a flat base, whose ruler
      // would sit under everything and never break.
      if (!this.h1 || bar.high >= this.h1.price - tol) this.newHigh(i, bar, this.h1 && bar.high <= this.h1.price ? this.h1.price : bar.high);
      if (!this.l1 || bar.low <= this.l1.price + tol) this.newLow(i, bar, this.l1 && bar.low >= this.l1.price ? this.l1.price : bar.low);
    }

    // (c) swing confirmation: bar p = i − N is now flanked by N completed bars
    const N = this.params.swingBars;
    const p = i - N;
    if (p >= N) {
      if (this.isPivot(p, 'high')) this.pivotHighs.push(p);
      if (this.isPivot(p, 'low')) this.pivotLows.push(p);
    }

    // (d) lay the rulers
    this.layUpper(i, bar, tol);
    this.layLower(i, bar, tol);
  }

  // ---- forming bar: wick breaks only (its close and volume are not final) ----
  probe(forming: TlBar) {
    const et = this.inSession(forming.time);
    if (!et) return;
    if (et.date !== this.sessionDate) {
      // first forming bar of a new session: yesterday's rulers go now, not a minute later
      if (this.sessionDate !== null) { this.reset(); this.sessionDate = et.date; }
      return;
    }
    this.checkBreaks(this.bars.length, forming, true);
  }

  private passesGuard(vol: number): boolean {
    const { volumeGuardPct, volumeGuardMinBars } = this.params;
    if (volumeGuardPct <= 0 || this.volumes.length < volumeGuardMinBars) return true;
    const sorted = this.volumes.slice().sort((a, b) => a - b);
    return vol >= percentile(sorted, volumeGuardPct);
  }

  // session bar by index — every index used below is within [0, bars.length)
  private bar(i: number): TlBar { return this.bars[i] as TlBar; }

  private isPivot(p: number, side: 'high' | 'low'): boolean {
    const N = this.params.swingBars, b = (i: number) => this.bar(i);
    for (let k = 1; k <= N; k++) {
      if (side === 'high') { if (!(b(p).high > b(p - k).high && b(p).high > b(p + k).high)) return false; }
      else if (!(b(p).low < b(p - k).low && b(p).low < b(p + k).low)) return false;
    }
    return true;
  }

  private retire(ln: TlLine | null, status: TlStatus, i: number, t: number) {
    if (!ln) return;
    if (ln.status === 'active') { ln.status = status; ln.endedAt = i; ln.endedTime = t; }
    if (this.params.keepHistory) this.history.push(ln);
    this.version++;
  }

  private newHigh(i: number, bar: TlBar, price: number) {
    this.retire(this.upper, 'superseded', i, bar.time); this.upper = null;
    this.h1 = { i, t: bar.time, price };
    this.upperMinIdx = 0;
    this.version++;
  }

  private newLow(i: number, bar: TlBar, price: number) {
    this.retire(this.lower, 'superseded', i, bar.time); this.lower = null;
    this.l1 = { i, t: bar.time, price };
    this.lowerMinIdx = 0;
    this.version++;
  }

  private mkLine(kind: TlKind, a: Pt, i2: number, i: number, t: number, tol: number): TlLine {
    const p2 = kind === 'upper' ? this.bar(i2).high : this.bar(i2).low;
    const ln: TlLine = {
      id: this.nextId++, kind, status: 'active', i1: a.i, i2, t1: a.t, t2: this.bar(i2).time, p1: a.price, p2,
      slope: (p2 - a.price) / (i2 - a.i), tol, touches: 0, drawableAt: i, drawableTime: t,
      brokenAt: -1, brokenTime: null, brokenPrice: null, closedAboveAt: -1, closedAboveTime: null, endedAt: -1, endedTime: null,
    };
    ln.touches = this.countTouches(ln, i);
    // A wick past the ruler after the second anchor but before it became
    // drawable is a break, marked where it happened — never hidden.
    for (let j = i2 + 1; j <= i; j++) {
      const v = lineVal(ln, j), b = this.bar(j);
      if (kind === 'upper' ? b.high > v + tol : b.low < v - tol) {
        ln.status = 'broken'; ln.brokenAt = j; ln.brokenTime = b.time; ln.brokenPrice = v; ln.endedAt = j; ln.endedTime = b.time;
        break;
      }
    }
    if (kind === 'upper') {
      for (let j = i2 + 1; j <= i; j++) { const v = lineVal(ln, j), b = this.bar(j); if (b.close > v) { ln.closedAboveAt = j; ln.closedAboveTime = b.time; break; } }
    }
    return ln;
  }

  // bars from the anchor to `upto` whose wick sits within tolerance of the ruler
  private countTouches(ln: TlLine, upto: number): number {
    let n = 0;
    for (let j = ln.i1; j <= upto; j++) {
      const v = lineVal(ln, j), b = this.bar(j);
      const d = ln.kind === 'upper' ? v - b.high : b.low - v;   // ≥ 0 when the wick is on the safe side
      if (Math.abs(d) <= ln.tol) n++;
    }
    return n;
  }

  // The ruler rests on the OUTERMOST bounce: among confirmed swings after the
  // anchor (and at/after the last break), the one whose line from the anchor
  // lies furthest out — highest line for resistance (max slope), lowest line
  // for support (min slope). Every other bounce then sits on the safe side.
  private pick(kind: TlKind, P: number[], a: Pt, minIdx: number): number {
    let best = -1, bestSlope = 0;
    for (let k = P.length - 1; k >= 0; k--) {
      const idx = P[k] as number;
      if (idx <= a.i) break;
      if (idx < minIdx) continue;
      const p2 = kind === 'upper' ? this.bar(idx).high : this.bar(idx).low;
      if (kind === 'upper' ? !(p2 < a.price) : !(p2 > a.price)) continue;
      const s = (p2 - a.price) / (idx - a.i);
      if (best < 0 || (kind === 'upper' ? s > bestSlope : s < bestSlope)) { best = idx; bestSlope = s; }
    }
    return best;
  }

  private layUpper(i: number, bar: TlBar, tol: number) {
    if (!this.h1 || (this.upper && this.upper.status === 'active')) return;
    const idx = this.pick('upper', this.pivotHighs, this.h1, this.upperMinIdx);
    if (idx < 0) return;
    // a broken ruler being replaced keeps its place in history (it was never superseded — it broke)
    if (this.upper) { if (this.params.keepHistory) this.history.push(this.upper); this.upper = null; }
    this.upper = this.mkLine('upper', this.h1, idx, i, bar.time, tol);
    if (this.upper.brokenAt >= 0) this.upperMinIdx = this.upper.brokenAt;
    this.version++;
  }

  private layLower(i: number, bar: TlBar, tol: number) {
    if (!this.l1 || (this.lower && this.lower.status === 'active')) return;
    const idx = this.pick('lower', this.pivotLows, this.l1, this.lowerMinIdx);
    if (idx < 0) return;
    if (this.lower) { if (this.params.keepHistory) this.history.push(this.lower); this.lower = null; }
    this.lower = this.mkLine('lower', this.l1, idx, i, bar.time, tol);
    if (this.lower.brokenAt >= 0) this.lowerMinIdx = this.lower.brokenAt;
    this.version++;
  }

  private checkBreaks(i: number, bar: TlBar, forming: boolean) {
    const up = this.upper;
    if (up && i > up.drawableAt) {
      const v = lineVal(up, i);
      if (up.status === 'active') {
        if (bar.high > v + up.tol) {
          up.status = 'broken'; up.brokenAt = i; up.brokenTime = bar.time; up.brokenPrice = v; up.endedAt = i; up.endedTime = bar.time;
          this.upperMinIdx = i;
          this.version++;
        } else if (!forming && v - bar.high <= up.tol) { up.touches++; this.version++; }
      }
      // "closed above" is a completed-bar observation, recorded separately from the wick break
      if (!forming && up.closedAboveAt < 0 && bar.close > v) { up.closedAboveAt = i; up.closedAboveTime = bar.time; this.version++; }
    }
    const lo = this.lower;
    if (lo && lo.status === 'active' && i > lo.drawableAt) {
      const v = lineVal(lo, i);
      if (bar.low < v - lo.tol) {
        lo.status = 'broken'; lo.brokenAt = i; lo.brokenTime = bar.time; lo.brokenPrice = v; lo.endedAt = i; lo.endedTime = bar.time;
        this.lowerMinIdx = i;
        this.version++;
      } else if (!forming && bar.low - v <= lo.tol) { lo.touches++; this.version++; }
    }
  }

  // ---- drawable frame ----
  frame(): TlFrame {
    const N = this.params.swingBars, n = this.bars.length;
    const nowI = n; // the forming bar's index — where "value now" is read
    const base = { n, N, sessionDate: this.sessionDate, tol: n ? this.tolFor(this.bar(n - 1).close) : 0, h1: this.h1, l1: this.l1 };
    if (!this.sessionDate) return { ok: false, reason: 'no-session', ...base, upperState: 'none', lowerState: 'none', upper: null, lower: null, upperLineId: -1, lowerLineId: -1, squeeze: false, lines: [], display: [], anchors: [], counts: { history: 0 } };
    const lines: TlLine[] = this.history.slice();
    if (this.upper) lines.push(this.upper);
    if (this.lower) lines.push(this.lower);
    // only the latest per side on the chart: the current ruler, else the one before it
    const lastOf = (kind: TlKind): TlLine | null => { for (let k = this.history.length - 1; k >= 0; k--) { const l = this.history[k] as TlLine; if (l.kind === kind) return l; } return null; };
    const display: TlLine[] = [];
    const dUp = this.upper || lastOf('upper'), dLo = this.lower || lastOf('lower');
    if (dUp) display.push(dUp);
    if (dLo) display.push(dLo);
    const anchors: TlAnchor[] = [];
    const curUp = this.upper, curLo = this.lower;
    if (curUp) {
      anchors.push({ label: 'H1', kind: 'upper', i: curUp.i1, t: curUp.t1, price: curUp.p1 });
      anchors.push({ label: 'H2', kind: 'upper', i: curUp.i2, t: curUp.t2, price: curUp.p2 });
    }
    if (curLo) {
      anchors.push({ label: 'L1', kind: 'lower', i: curLo.i1, t: curLo.t1, price: curLo.p1 });
      anchors.push({ label: 'L2', kind: 'lower', i: curLo.i2, t: curLo.t2, price: curLo.p2 });
    }
    return {
      ok: n >= 2 * N + 1, reason: n >= 2 * N + 1 ? null : 'warming', ...base,
      upperState: curUp ? (curUp.status === 'active' ? 'ok' : 'broken') : 'none',
      lowerState: curLo ? (curLo.status === 'active' ? 'ok' : 'broken') : 'none',
      upper: curUp ? { dir: 'falling', crossed: curUp.brokenAt >= 0, closedAbove: curUp.closedAboveAt >= 0, touches: curUp.touches, valueNow: lineVal(curUp, nowI) } : null,
      lower: curLo ? { dir: 'rising', broken: curLo.brokenAt >= 0, touches: curLo.touches, valueNow: lineVal(curLo, nowI) } : null,
      upperLineId: curUp ? curUp.id : -1,
      lowerLineId: curLo ? curLo.id : -1,
      squeeze: !!(curUp && curLo && curUp.slope < 0 && curLo.slope > 0),
      lines, display, anchors,
      counts: { history: this.history.length },
    };
  }
}

// Convenience for tests and one-shot use: replay a candle array (all but the
// last bar completed, the last bar forming) and return the frame.
export function computeTrendlines(candles: TlBar[], params?: Partial<TlParams>, spread?: number): TlFrame {
  const e = new PullbackTrendEngine(params);
  e.setSpread(spread);
  for (let i = 0; i < candles.length - 1; i++) e.push(candles[i] as TlBar);
  if (candles.length) e.probe(candles[candles.length - 1] as TlBar);
  return e.frame();
}

// Compact readout summary — the only thing the layer pushes through React
// state, so a frame that changed nothing visible costs no re-render.
export function trendSummaryKey(fr: TlFrame | null): string {
  if (!fr) return 'none';
  if (!fr.ok) return fr.reason || 'warming';
  return [
    fr.upperState + (fr.upper ? ':' + fr.upper.valueNow.toFixed(4) + (fr.upper.crossed ? 'x' : '') + (fr.upper.closedAbove ? 'c' : '') + 't' + fr.upper.touches : ''),
    fr.lowerState + (fr.lower ? ':' + fr.lower.valueNow.toFixed(4) + (fr.lower.broken ? 'x' : '') + 't' + fr.lower.touches : ''),
    fr.squeeze ? 'sq' : '',
    fr.counts.history,
  ].join('|');
}
